import * as d3 from 'importers/d3-importer'

/**
 * Creates an SVG multi-line graph using D3.
 *
 * @class AmortizationGraph
 */
export class AmortizationGraph {
  duration = 800;
  labelColors = [ '#41ad38', '#e37475', '#5f7795' ];
  margin = {
    top:    24,
    right:  34,
    bottom: 30,
    left:   0
  };
  width = 1200 - this.margin.left - this.margin.right // eslint-disable-line no-invalid-this
  height = 400 - this.margin.top - this.margin.bottom // eslint-disable-line no-invalid-this

  /**
   * Constructor - called on instantiating this new object
   *
   * @constructs
   * @param {Element} element container
   * @param {String} yAxisLabel - The name of the label that will be used as the Y axis and excluded from drawing the line
   */
  constructor(element, yAxisLabel) {
    this.element = element
    this.yAxisLabel = yAxisLabel
    this.color = d3.scaleOrdinal().range(this.labelColors)

    this._buildGraph(element)
  }

  /**
   * Builds the graph with the given dataset
   *
   * @param {Array} dataset - Array of objects, each representing the line with `label` and `data`
   * @return {AmortizationGraph}
   * @example
   *   dataset = [
   *     {label: "Yrs", data: [0, 1, 2, ...]}
   *     {label: "Interest", data: [1023, 1021, 1020 , ...]}
   *     {label: "Principal", data: [419, 421, 422, ...]}
   *     {label: "Remaining", data: [297081, 296660, 296238, ...]}
   *   ]
   */
  build(dataset) {
    this._drawGraph(dataset)

    // Add event listener to responsive sizing
    this._sizeGraph(this.element, dataset)

    return this
  }


  // private

  /**
   * Rounds and formats number to USD monetary format
   *
   * @param {number} value - Formatted USD currency
   * @private
   *
   * @return {String} formatted string
   *
   * @example
   *
   * input = 1442.2431533
   * return = '$1,442'
   */
  _formatToUSD(value) {
    return d3.format('$,.0f')(value)
  }


  /**
   * Gets the maximum values from each data set and returns an array of max values
   *
   * @param {Array} dataset - Array of objects, each representing the line with `label` and `data`
   * @private
   *
   * @return {number}
   */
  _getMax(dataset) {
    return Math.max(...dataset.map(d => Math.max(...d.data)))
  }


  /**
   * Returns the dataset excluding the data set line that is used for the X axis
   *
   * @param {Array} dataset - Array of objects, each representing the line with `label` and `data`
   * @private
   *
   * @return {Array.<Object>} - Array of objects without the data set for the x axis
   */
  _excludeKeyFromDataset(dataset) {
    return dataset.filter(d => d.label !== this.yAxisLabel)
  }


  /**
   * Rounds (to ceiling) the maximum number from the dataset to the nearest last placeholder.
   * Rounds no less than 1k and no more than 1b
   *
   * @example 1,900 will become 2,000 and 2,200,000 will become 3,000,000
   * @param {Array} dataset - Array of objects, each representing the line with `label` and `data`
   * @return {number}
   * @private
   */
  _roundToTheNearestLastPlace(dataset) {
    const maxNumber = this._getMax(dataset)
    let roundByNumber = 0

    if (maxNumber <= 10000) {
      roundByNumber = 1000
    } else if (maxNumber <= 100000) {
      roundByNumber = 10000
    } else if (maxNumber <= 1000000) {
      roundByNumber = 100000
    } else if (maxNumber <= 10000000) {
      roundByNumber = 1000000
    } else {
      roundByNumber = 10000000
    }

    return Math.ceil(maxNumber / roundByNumber) * roundByNumber
  }


  /**
   * Draws the graph from the given dataset. This function is called on the initial `build()` and on the `window`
   * resize event
   *
   * @param {Array} dataset - Array of objects, each representing the line with `label` and `data`
   * @private
   */
  _drawGraph(dataset) {
    const datasetExcludingKey = this._excludeKeyFromDataset(dataset)

    // Buils scale
    this._buildScale(dataset)

    // Build lines
    this._buildLines(datasetExcludingKey)

    // Build tooltips
    this._buildTooltips(datasetExcludingKey)

    // Build mouse over
    this.buildMouseOverlay(datasetExcludingKey)
  }


  /**
   * Builds the graph from the given dataset.
   *
   * @param {Element} element container
   * @private
   */
  _buildGraph(element) {
    this.svgContainer = d3.select(element)

    // Create SVG element
    this.svg =
      this.svgContainer
          .append('svg')
          .classed('amortization-graph__graph', true)
          .attr('width', this.width + (this.margin.left + this.margin.right))
          .attr('height', this.height + (this.margin.top + this.margin.bottom))
          .append('g')
          .attr('transform', `translate(${[ this.margin.left, this.margin.top ]})`)

    // Create X Axis
    this.xAxisG =
      this.svg
          .append('g')
          .classed('amortization-graph__graph-x-axis', true)
          .attr('transform', 'translate(0,' + this.height + ')')

    // Create Y Axis
    this.yAxisG =
      this.svg
          .append('g')
          .classed('amortization-graph__graph-y-axis', true)

    // Styles Y Axis
    this.yAxisG.select('.domain').remove()
    this.yAxisG.selectAll('.tick:not(:first-of-type) line').attr('stroke', '#e0e0e0')
    this.yAxisG.selectAll('.tick text').attr('x', 4).attr('dy', -4)

    // Draw tooltips
    this.tooltip =
      this.svg
          .append('g')
          .classed('amortization-graph__graph-tooltips', true)
          .style('display', 'none')

    // Focus line
    this.tooltip
        .append('line')
        .classed('amortization-graph__graph-tooltip-line', true)
        .attr('y1', 0)
        .attr('y2', this.height)

    this.legend = this.svgContainer
        .append('div')
        .classed('amortization-graph__legend', true)

    this.svgContainer
        .select('svg')
        .append('rect')
        .attr('width', this.width)
        .attr('height', this.height)
        .attr('transform', `translate(${[ this.margin.left, this.margin.top ]})`)
        .classed('amortization-graph__graph-overlay', true)
  }


  /**
   * Builds the graph scale
   *
   * @param {Array} dataset - Array of objects, each representing the line with `label` and `data`
   * @private
   */
  _buildScale(dataset) {
    // Create scale functions
    this.keyColumn = dataset.find(key => key.label === this.yAxisLabel).data

    this.xScale = d3.scaleLinear().rangeRound([ 0, this.width ]).domain(d3.extent(this.keyColumn))
    this.yScale = d3.scaleLinear().rangeRound([ this.height, 0 ]).domain([ 0, this._roundToTheNearestLastPlace(dataset) ])

    // Draw Axes
    const xAxis = d3.axisBottom(this.xScale).ticks(5).tickFormat(d => `${d} ${this.yAxisLabel}`)

    this.svg
        .select('.amortization-graph__graph-x-axis')
        .call(xAxis)
        .append('text')
        .classed('amortization-graph__graph-label', true)
        .attr('x', this.width / 2)
        .attr('y', 30)
        .style('amortization-graph__graph-text-anchor', 'middle')
        .text(this.yAxisLabel)

    const yAxis = d3.axisRight(this.yScale).tickSize(this.width).ticks(3).tickFormat(d3.format('$,.0s'))

    this.svg.select('.amortization-graph__graph-y-axis').call(yAxis)
  }


  /**
   * Builds lines into graph
   *
   * @param {Array} dataset - Array of objects, each representing the line with `label` and `data`
   * @private
   */
  _buildLines(dataset) {
    // Create lines
    const line =
      d3.line()
          .defined(d => d !== null && d !== undefined)
          .curve(d3.curveBasis)
          .x((d, i) => this.xScale(this.keyColumn[i]))
          .y(d => this.yScale(d))

    // Changing data
    const paths = this.svg.selectAll('.amortization-graph__graph-line').data(dataset)

    paths.enter()
        .insert('path')
        .classed('amortization-graph__graph-line', true)
        .attr('d', d => line(d.data))
        .style('stroke', d => this.color(d.label))
        .attr('data-series', d => d.label)

    // Path animation during updating
    paths.transition()
        .ease(d3.easePoly)
        .duration(this.duration)
        .attr('d', d => line(d.data))
  }


  /**
   * Builds the tooltips
   *
   * @param {Array} dataset - Array of objects, each representing the line with `label` and `data`
   * @private
   */
  _buildTooltips(dataset) {
    // Create tooltip for mouse over
    const tooltip = this.svg
        .select('.amortization-graph__graph-tooltips')
        .selectAll('.amortization-graph__graph-tooltip')
        .data(dataset)

    tooltip.enter()
        .append('g')
        .classed('amortization-graph__graph-tooltip', true)
        .append('circle')
        .attr('r', 5)
        .attr('fill', d => this.color(d.label))
        .attr('stroke', d => this.color(d.label))
        .classed('amortization-graph__graph-tooltip-circle', true)
        .append('rect')
        .attr('x', 8)
        .attr('y', '-5')
        .attr('width', 22)
        .attr('height', '0.75em')
        .append('text')
        .attr('x', 9)
        .attr('dy', '.35em')

    // Create tooltip children
    const series = this.legend.selectAll('.amortization-graph__graph-series').data(dataset)

    const seriesContainer = series.enter()
        .append('div')
        .classed('amortization-graph__graph-series', true)

    seriesContainer
        .append('span')
        .classed('amortization-graph__graph-series-marker', true)
        .style('background-color', d => this.color(d.label))

    seriesContainer
        .append('span')
        .classed('amortization-graph__graph-series-label', true)
        .text(d => d.label)

    seriesContainer
        .append('span')
        .classed('amortization-graph__graph-series-value', true)
        .text(d => this._formatToUSD(d.data[0]))

    // Updates the values in realtime
    series.transition()
        .style('display', function(d) {
          // If the line ends (undefined), hides the information label
          if (d.data === undefined || d.data.length === 0) {
            return 'none'
          } else {
            d3.select(this).style('display', null) // eslint-disable-line no-invalid-this
          }
        })
        .select('.amortization-graph__graph-series-value')
        .text(d => this._formatToUSD(d.data[0]))
  }


  /**
   * Builds the overlay of the graph
   *
   * @param {Array} dataset -  Array of objects, each representing the line with `label` and `data`
   * @private
   */
  buildMouseOverlay(dataset) {
    const _this = this

    _this.svgContainer
        .select('.amortization-graph__graph-overlay')
        .on('mouseover', () => _this.tooltip.style('display', null))
        .on('mouseout', () => _this.tooltip.style('display', 'none'))
        .on('mousemove', function() {
          _this._mousemove(this, dataset) // eslint-disable-line no-invalid-this
        })
  }


  /**
   * Mouse over graph and output details on each Y point
   *
   * @param {event} mousemove - Event `mousemove`
   * @param {Array} dataset - Array of objects, each representing the line with `label` and `data`
   * @private
   */
  _mousemove(mousemove, dataset) {
    const x0 = this.xScale.invert(d3.mouse(mousemove)[0])
    const mouseIndex = Math.round(x0)
    const maxY = [ this.height ]
    // Get the closest index based on the key column
    const xAxisIndex = this.keyColumn.indexOf((mouseIndex <= this.keyColumn.length - 1) ? mouseIndex : this.keyColumn.length - 1)

    // Updates line & circles on graph
    this.svg
        .select('.amortization-graph__graph-tooltips')
        .selectAll('.amortization-graph__graph-tooltip')
        .attr('transform', d => {
          // Push the max value from the set
          maxY.push(Math.max(...dataset.map(d => d.data[xAxisIndex])))

          const x = this.xScale(this.keyColumn[xAxisIndex])
          const y = this.yScale(d.data[xAxisIndex])

          if (!isNaN(x) && !isNaN(y)) {
            return `translate(${[ x, y ]})`
          }
        })

    this.tooltip
        .select('.amortization-graph__graph-tooltip-line')
        .attr('transform', () => `translate(${this.xScale(this.keyColumn[xAxisIndex])})`)
        .attr('y1', () => {
          const minY = Math.min(this.height, this.yScale(Math.max(...maxY)))

          if (minY) {
            return minY
          }
        })

    // Updates legend values
    this.legend
        .selectAll('.amortization-graph__graph-series')
        .style('display', function(d) {
          // If the line ends (undefined), hides the information label
          if (d.data[xAxisIndex] === undefined) {
            return 'none'
          } else {
            d3.select(this).style('display', null) // eslint-disable-line no-invalid-this
          }
        })
        .select('.amortization-graph__graph-series-value')
        .text(d => this._formatToUSD(d.data[xAxisIndex] || 0) || '$0')
  }


  /**
   * Resizes the graph on the `window` resize event.
   *
   * @param {Element} element container
   * @param {Array} dataset - array of objects, each representing the line with `label` and `data`
   * @private
   */
  _sizeGraph(element, dataset) {
    const resizeSVG = (e, d) => {
      this.width =  parseInt(e.offsetWidth || 1200, 10) - this.margin.left - this.margin.right

      e.querySelector('svg').setAttribute('width', this.width)

      this._drawGraph(d)
    }

    resizeSVG(element, dataset)

    window.addEventListener('resize', () => { resizeSVG(element, dataset) })
  }
}
